import type { CountryCode, PhoneNumber } from 'libphonenumber-js';
import {
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber as isValid,
  parsePhoneNumber as parsePhone,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import type { Maybe } from 'yup';

import { COUNTRY_CALLING_CODES } from './phonePrefixes/const';
export const isValidPhoneNumber = (phoneNumber: string) => {
  try {
    return isValid(phoneNumber);
  } catch (e) {
    return false;
  }
};
export const getPhoneCode = (phoneNumber: string) => {
  try {
    const number = getCountryCallingCode(phoneNumber as CountryCode);
    return number.toString();
  } catch (e) {
    return { code: -1, msg: 'Failed to getCountryCallingCode' };
  }
};

export const parsePhoneNumber = (phone: string): Maybe<PhoneNumber> => {
  try {
    if (!isValidPhoneNumber(phone)) return null;
    return parsePhone(phone);
  } catch (error) {
    return null;
  }
};

export const getPhoneCodeAndNumber = (phone?: string | null) => {
  const defaultPhonePrefix = '+1';
  if (phone && isValidPhoneNumber(phone)) {
    const phoneNumber = parsePhoneNumberWithError(phone);
    const phonePrefix =
      COUNTRY_CALLING_CODES.find((item) => item.code === phoneNumber.country)
        ?.value ?? defaultPhonePrefix;
    return {
      phoneCode: phonePrefix,
      phoneNumber: formatNationalByPhonePrefix(
        phonePrefix,
        phoneNumber.formatNational(),
        false,
      ),
    };
  }

  return {
    phoneCode: defaultPhonePrefix,
  };
};

export const getExamplePhone = (code: CountryCode) => {
  const phoneNumber = getExampleNumber(code, examples);
  const formatNational = phoneNumber?.formatNational();
  const prefix = COUNTRY_CALLING_CODES.find((e) => e.code === code)?.value;
  if (!formatNational || !prefix) return '';
  const areaCodeByPrefix = prefix.split('-')[1];
  const pattern = new RegExp(`^\\(${areaCodeByPrefix}\\)\\s+`);
  if (areaCodeByPrefix && pattern.test(formatNational)) {
    return formatNational.replace(pattern, '');
  }
  return formatNational;
};

export const formatNationalByCountryCode = (
  code: CountryCode,
  national?: string,
) => {
  const prefix = COUNTRY_CALLING_CODES.find((e) => e.code === code)?.value;
  return formatNationalByPhonePrefix(prefix, national);
};

export const formatNationalByPhonePrefix = (
  prefix?: string,
  national?: string,
  isFormat = true,
) => {
  if (!national) return '';

  let phoneNumberWithoutPrefix = national;
  const formatPattern = /\D/g;

  if (prefix) {
    const areaCodeByPrefix = prefix.split('-')[1]?.trim();

    if (areaCodeByPrefix && /^\d+$/.test(areaCodeByPrefix)) {
      const pattern = new RegExp(`^\\(${areaCodeByPrefix}\\)\\s+`);

      phoneNumberWithoutPrefix = phoneNumberWithoutPrefix.replace(pattern, '');
    }
  }

  return isFormat
    ? phoneNumberWithoutPrefix
    : phoneNumberWithoutPrefix.replace(formatPattern, '');
};

export const formatPhoneWithCallingCode = (
  phone?: string,
  countryCode?: string,
) => {
  const rawPhoneNumber = phone?.trim();
  if (!rawPhoneNumber || rawPhoneNumber === '') return '';
  if (!countryCode || countryCode.trim() === '') return phone || '';

  // If already starts with +, return as-is
  if (rawPhoneNumber.startsWith('+')) return rawPhoneNumber;

  const codeUpper = countryCode?.toUpperCase();
  const callingCode = COUNTRY_CALLING_CODES.find(
    (c: { code: string; value: string }) => c.code === codeUpper,
  )?.value;

  if (!callingCode) {
    return `+${rawPhoneNumber}`;
  }

  const digitsOnly = (val: string) => val.replace(/\D+/g, '');
  // Paypal or other sources might return the phone number with spaces or parenthesis
  const rawPhoneNumberDigits = digitsOnly(rawPhoneNumber);
  const callingDigits = digitsOnly(callingCode);

  const hasCallingCode =
    rawPhoneNumberDigits.startsWith(callingDigits) ||
    (callingDigits.length > 1 && // Other than US and CA for instance
      callingDigits.startsWith('1') && // Special case for NANP sub-codes like +1-684
      rawPhoneNumberDigits.startsWith(callingDigits.slice(1)));

  if (hasCallingCode) {
    return `+${rawPhoneNumber}`;
  }

  return `${callingCode}${rawPhoneNumber}`;
};
